import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConfBadge, EmptyState, PageHeader, StateBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Commitments() {
  const { tenantId, can } = useAuth();
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", due_date: "", priority: "medium", assignee_id: "" });

  const load = () => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/action-items`, { params: { framework: "panchatanthra" } })
      .then(({ data }) => setItems(data)).catch((e) => toast.error(errMsg(e)));
    api.get(`/tenants/${tenantId}/grants`).then(({ data }) => setMembers(data.filter((g) => g.user))).catch(() => {});
  };
  useEffect(load, [tenantId]); // eslint-disable-line

  const create = async () => {
    try {
      await api.post(`/tenants/${tenantId}/action-items`, {
        ...form,
        framework: "panchatanthra",
        confidentiality: 1,
        source_type: "assessment",
      });
      toast.success("Commitment created and assignee notified");
      setOpen(false);
      setForm({ title: "", due_date: "", priority: "medium", assignee_id: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-10">
      <PageHeader
        testid="commitments-header"
        eyebrow="Panchatanthra"
        title="Commitments & actions"
        description="Strategic commitments generated from assessment outcomes, tracked to closure."
        actions={
          can("create") && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-commitment-button" className="h-9 rounded-none text-[10px] uppercase tracking-[0.18em]">
                  New commitment
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader><DialogTitle className="font-display text-2xl">New commitment</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="label-caps">Title</Label>
                    <Input data-testid="commitment-title-input" className="rounded-none" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Due date</Label>
                    <Input data-testid="commitment-due-input" type="date" className="rounded-none" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Owner</Label>
                    <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
                      <SelectTrigger data-testid="commitment-owner-select" className="rounded-none"><SelectValue placeholder="Assign owner" /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        {members.map((m) => <SelectItem key={m.id} value={m.user_id}>{m.user?.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger data-testid="commitment-priority-select" className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button data-testid="create-commitment-submit" className="w-full rounded-none" disabled={!form.title} onClick={create}>Create</Button>
                </div>
              </DialogContent>
            </Dialog>
          )
        }
      />

      <div className="panel divide-y divide-border" data-testid="commitments-list">
        {items.map((i) => (
          <div key={i.id} className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
            <div className="min-w-0">
              <p className="text-sm">{i.title}</p>
              <p className="label-caps mt-1">{i.priority} priority · due {i.due_date || "—"}</p>
            </div>
            <div className="flex items-center gap-3">
              <ConfBadge level={i.confidentiality} />
              <StateBadge state={i.status} />
              {can("edit") && i.status !== "done" && (
                <Button
                  data-testid={`close-commitment-${i.id}`}
                  size="sm"
                  variant="outline"
                  className="rounded-none text-[10px] uppercase tracking-[0.15em]"
                  onClick={async () => {
                    await api.put(`/tenants/${tenantId}/action-items/${i.id}`, { status: "done" });
                    load();
                  }}
                >
                  Close
                </Button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <EmptyState testid="commitments-empty" title="No commitments" message="Nothing tracked for this framework yet." />}
      </div>
    </div>
  );
}
